import { cutBatches, stripPlan, sashingPlan, cornerstonePlan, settingTrianglePlan, borderPlan, type CutBatch, type StripPlan } from "./geometry"
import { findFabricByName, findFabricByRole, findFabricBySlot } from "./state"
import type { Project } from "./state"

export interface FabricUsage {
  fabric: string
  haveInches: number
  needInches: number
  ok: boolean
  headroomInches: number
  batches: Array<{ batch: CutBatch; plan: StripPlan }>
}

export function computeUsage(project: Project): FabricUsage[] {
  const batches = cutBatches(project)
  const byFabric = new Map<string, Array<{ batch: CutBatch; plan: StripPlan }>>()

  for (const batch of batches) {
    const fabric = findFabricByName(project, batch.fabric)
    if (!fabric) continue
    const plan = stripPlan(batch, fabric.width)
    const existing = byFabric.get(batch.fabric) ?? []
    existing.push({ batch, plan })
    byFabric.set(batch.fabric, existing)
  }

  const sashExtra = new Map<string, number>()
  const sash = sashingPlan(project)
  if (sash !== null) {
    const sashFabric = findFabricByRole(project, "sashing")
    if (sashFabric) {
      sashExtra.set(sashFabric.name, sash.sashInches + sash.cornerInches)
      if (!byFabric.has(sashFabric.name)) byFabric.set(sashFabric.name, [])
    }
  }
  const cs = cornerstonePlan(project)
  if (cs !== null) {
    const csFabric = findFabricByRole(project, "cornerstone")
    if (csFabric) {
      sashExtra.set(csFabric.name, cs.cornerInches)
      if (!byFabric.has(csFabric.name)) byFabric.set(csFabric.name, [])
    }
  }
  const setting = settingTrianglePlan(project)
  if (setting !== null) {
    // Setting triangles bill against the dedicated `setting` fabric if one
    // exists; otherwise fall back to `background` (matching the same lookup
    // settingTrianglePlan uses to find its fabric, so we always bill the
    // same fabric the cut math priced).
    const settingFabric = findFabricByRole(project, "setting") ?? findFabricBySlot(project, "background")
    if (settingFabric) {
      const prev = sashExtra.get(settingFabric.name) ?? 0
      sashExtra.set(settingFabric.name, prev + setting.sideInches + setting.cornerInches)
      if (!byFabric.has(settingFabric.name)) byFabric.set(settingFabric.name, [])
    }
  }
  const borders = borderPlan(project)
  if (borders !== null) {
    // Borders share the setting fabric pool by default.
    const settingFabric = findFabricByRole(project, "setting") ?? findFabricBySlot(project, "background")
    if (settingFabric) {
      const prev = sashExtra.get(settingFabric.name) ?? 0
      sashExtra.set(settingFabric.name, prev + borders.totalInches)
      if (!byFabric.has(settingFabric.name)) byFabric.set(settingFabric.name, [])
    }
  }

  const usages: FabricUsage[] = []
  for (const [fabricName, pairs] of byFabric) {
    const fabric = findFabricByName(project, fabricName)
    if (!fabric) continue
    const batchInches = pairs.reduce((s, p) => s + p.plan.inchesUsed, 0)
    const needInches = batchInches + (sashExtra.get(fabricName) ?? 0)
    const haveInches = fabric.yards * 36.0
    usages.push({
      fabric: fabricName,
      haveInches,
      needInches,
      ok: needInches <= haveInches,
      headroomInches: haveInches - needInches,
      batches: pairs,
    })
  }
  return usages
}
